/**
 * 절차적 디테일 텍스처 (에셋 없이 표면 사실감↑).
 * 타일러블 value-noise fbm으로 노멀맵/러프니스맵을 생성해 평평한 머티리얼에 미세 요철·
 * 러프니스 변화를 준다. 외부 파일 의존 없음.
 */
import * as THREE from 'three';

function hash2(ix: number, iy: number, seed: number): number {
  let h = (ix * 374761393 + iy * 668265263 + seed * 2246822519) >>> 0;
  h = (h ^ (h >>> 13)) >>> 0;
  h = Math.imul(h, 1274126177) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

/** 주기(period)로 타일러블한 value noise. */
function vnoise(x: number, y: number, period: number, seed: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = smooth(x - ix);
  const fy = smooth(y - iy);
  const w = (i: number, j: number) => hash2(((i % period) + period) % period, ((j % period) + period) % period, seed);
  const a = w(ix, iy);
  const b = w(ix + 1, iy);
  const c = w(ix, iy + 1);
  const d = w(ix + 1, iy + 1);
  return a * (1 - fx) * (1 - fy) + b * fx * (1 - fy) + c * (1 - fx) * fy + d * fx * fy;
}

function fbm(u: number, v: number, seed: number): number {
  let f = 0;
  let amp = 0.5;
  let period = 8;
  for (let o = 0; o < 4; o++) {
    f += amp * vnoise(u * period, v * period, period, seed + o);
    amp *= 0.5;
    period *= 2;
  }
  return f;
}

/** 높이장 → 노멀맵(DataTexture, 선형). strength로 요철 강도 조절. */
export function noiseNormalMap(size = 256, strength = 1, repeat = 3, seed = 7): THREE.DataTexture {
  const h = new Float32Array(size * size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      h[y * size + x] = fbm(x / size, y / size, seed);
    }
  }
  const data = new Uint8Array(size * size * 4);
  const at = (x: number, y: number) => h[((y % size) + size) % size * size + (((x % size) + size) % size)];
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (at(x + 1, y) - at(x - 1, y)) * strength;
      const dy = (at(x, y + 1) - at(x, y - 1)) * strength;
      // normal = normalize(-dx, -dy, 1)
      const len = Math.hypot(dx, dy, 1);
      const nx = -dx / len;
      const ny = -dy / len;
      const nz = 1 / len;
      const i = (y * size + x) * 4;
      data[i] = (nx * 0.5 + 0.5) * 255;
      data[i + 1] = (ny * 0.5 + 0.5) * 255;
      data[i + 2] = (nz * 0.5 + 0.5) * 255;
      data[i + 3] = 255;
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  tex.colorSpace = THREE.NoColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(repeat, repeat);
  tex.needsUpdate = true;
  return tex;
}

/** 러프니스 변화 맵(DataTexture, 선형). base±range. */
export function noiseRoughnessMap(size = 256, base = 0.6, range = 0.25, repeat = 3, seed = 21): THREE.DataTexture {
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const n = fbm(x / size, y / size, seed);
      const r = Math.min(1, Math.max(0, base + (n - 0.5) * 2 * range));
      const v = r * 255;
      const i = (y * size + x) * 4;
      data[i] = data[i + 1] = data[i + 2] = v;
      data[i + 3] = 255;
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  tex.colorSpace = THREE.NoColorSpace;
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(repeat, repeat);
  tex.needsUpdate = true;
  return tex;
}
